/**
 * Leviathan Web - Rack Prices Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Daily wholesale fuel prices from terminals/suppliers
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Product {
  id: string;
  sku: string;
  name: string;
  costPerUnit: number | null;
}

interface RackPrice {
  id: string;
  price: number;
  effectiveDate: string;
  terminal: string | null;
  supplier: string | null;
}

interface ProductPrice {
  product: Product;
  rackPrice: RackPrice | null;
}

interface Stats {
  totalPrices: number;
  recentPrices: number;
  staleProducts: number;
  terminals: Array<{ name: string | null; count: number }>;
}

interface PriceHistory {
  id: string;
  price: number;
  effectiveDate: string;
  terminal: string | null;
  supplier: string | null;
  product: {
    id: string;
    sku: string;
    name: string;
    category: string;
  };
}

const RackPricesPage = () => {
  const { accessToken } = useAuth();
  const [latestPrices, setLatestPrices] = useState<ProductPrice[]>([]);
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<'current' | 'history'>('current');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPrices, setBulkPrices] = useState<Record<string, string>>({});
  const [bulkTerminal, setBulkTerminal] = useState('');
  const [bulkSupplier, setBulkSupplier] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchStats();
    if (view === 'current') {
      fetchLatestPrices();
    } else {
      fetchHistory();
    }
  }, [view]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rackprices/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchLatestPrices = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/rackprices/latest`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLatestPrices(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch latest prices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/rackprices?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openBulkModal = () => {
    // Pre-populate with current prices
    const prices: Record<string, string> = {};
    latestPrices.forEach((pp) => {
      prices[pp.product.id] = pp.rackPrice?.price?.toString() || '';
    });
    setBulkPrices(prices);
    setShowBulkModal(true);
  };

  const handleBulkSave = async () => {
    setIsSaving(true);
    try {
      const prices = Object.entries(bulkPrices)
        .filter(([_, price]) => price !== '')
        .map(([productId, price]) => ({
          productId,
          price: parseFloat(price),
        }));

      if (prices.length === 0) {
        alert('No prices to update');
        return;
      }

      const response = await fetch(`${API_URL}/api/rackprices/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          prices,
          terminal: bulkTerminal || undefined,
          supplier: bulkSupplier || undefined,
        }),
      });

      if (response.ok) {
        setShowBulkModal(false);
        fetchLatestPrices();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to save prices');
      }
    } catch (error) {
      console.error('Failed to save prices:', error);
      alert('Failed to save prices');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(4)}`;
  };

  const getPriceAge = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  const getPriceAgeStatus = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) return 'fresh';
    if (diffDays <= 7) return 'recent';
    return 'stale';
  };

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Rack Prices
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Daily Wholesale Fuel Prices from Terminals
              </p>
            </div>
            <button
              onClick={openBulkModal}
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">Update Prices</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Records" value={stats.totalPrices} />
          <StatCard label="Last 30 Days" value={stats.recentPrices} status="operational" />
          <StatCard
            label="Stale Products"
            value={stats.staleProducts}
            status={stats.staleProducts > 0 ? 'warning' : 'operational'}
          />
          <StatCard label="Terminals" value={stats.terminals.length} />
        </div>
      )}

      {/* View Toggle */}
      <GeometricPanel>
        <div className="flex space-x-4">
          <button
            onClick={() => setView('current')}
            className={`flex-1 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
              view === 'current'
                ? 'bg-amber text-background'
                : 'bg-background text-text-secondary hover:text-amber border border-panel-border'
            }`}
          >
            Current Prices
          </button>
          <button
            onClick={() => setView('history')}
            className={`flex-1 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
              view === 'history'
                ? 'bg-amber text-background'
                : 'bg-background text-text-secondary hover:text-amber border border-panel-border'
            }`}
          >
            Price History
          </button>
        </div>
      </GeometricPanel>

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-12 text-text-tertiary font-mono animate-pulse">
          &gt; Loading rack prices...
        </div>
      ) : view === 'current' ? (
        <div className="space-y-4">
          {latestPrices.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No fuel products found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                Add fuel products to start tracking rack prices
              </p>
            </div>
          ) : (
            <GeometricPanel>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-panel-border">
                      <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Product
                      </th>
                      <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Current Cost
                      </th>
                      <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Rack Price
                      </th>
                      <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Terminal
                      </th>
                      <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Last Updated
                      </th>
                      <th className="text-center py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestPrices.map((pp) => {
                      const ageStatus = pp.rackPrice
                        ? getPriceAgeStatus(pp.rackPrice.effectiveDate)
                        : null;
                      return (
                        <tr
                          key={pp.product.id}
                          className="border-b border-panel-border/50 hover:bg-panel-dark/50"
                        >
                          <td className="py-3 px-4">
                            <div className="font-mono text-text-primary">
                              {pp.product.name}
                            </div>
                            <div className="text-text-tertiary text-xs">
                              {pp.product.sku}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-text-secondary">
                            {pp.product.costPerUnit
                              ? formatPrice(pp.product.costPerUnit)
                              : '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="font-mono text-amber text-lg">
                              {pp.rackPrice ? formatPrice(pp.rackPrice.price) : '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-mono text-text-secondary text-sm">
                            {pp.rackPrice?.terminal || '-'}
                          </td>
                          <td className="py-3 px-4 font-mono text-text-tertiary text-sm">
                            {pp.rackPrice ? (
                              <div>
                                <div>{formatDate(pp.rackPrice.effectiveDate)}</div>
                                <div className="text-xs">
                                  {getPriceAge(pp.rackPrice.effectiveDate)}
                                </div>
                              </div>
                            ) : (
                              'Never'
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {pp.rackPrice ? (
                              <span
                                className={`inline-block w-3 h-3 rounded-full ${
                                  ageStatus === 'fresh'
                                    ? 'bg-success-green'
                                    : ageStatus === 'recent'
                                      ? 'bg-warning-amber'
                                      : 'bg-error-red'
                                }`}
                                style={{ boxShadow: '0 0 8px currentColor' }}
                                title={
                                  ageStatus === 'fresh'
                                    ? 'Fresh'
                                    : ageStatus === 'recent'
                                      ? 'Recent'
                                      : 'Stale'
                                }
                              />
                            ) : (
                              <span
                                className="inline-block w-3 h-3 rounded-full bg-gray-500"
                                title="No price"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </GeometricPanel>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {priceHistory.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No price history found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                Start tracking rack prices to build history
              </p>
            </div>
          ) : (
            <GeometricPanel>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-panel-border">
                      <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Date
                      </th>
                      <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Product
                      </th>
                      <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Price
                      </th>
                      <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Terminal
                      </th>
                      <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Supplier
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceHistory.map((ph) => (
                      <tr
                        key={ph.id}
                        className="border-b border-panel-border/50 hover:bg-panel-dark/50"
                      >
                        <td className="py-3 px-4 font-mono text-text-secondary">
                          {formatDate(ph.effectiveDate)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-mono text-text-primary">
                            {ph.product.name}
                          </div>
                          <div className="text-text-tertiary text-xs">
                            {ph.product.sku}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-amber">
                          {formatPrice(ph.price)}
                        </td>
                        <td className="py-3 px-4 font-mono text-text-secondary text-sm">
                          {ph.terminal || '-'}
                        </td>
                        <td className="py-3 px-4 font-mono text-text-secondary text-sm">
                          {ph.supplier || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GeometricPanel>
          )}
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative max-w-2xl w-full mx-4">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-panel-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-display text-steel uppercase tracking-wider">
                  Update Rack Prices
                </h2>
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="text-text-tertiary hover:text-amber"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Terminal
                    </label>
                    <input
                      type="text"
                      value={bulkTerminal}
                      onChange={(e) => setBulkTerminal(e.target.value)}
                      placeholder="Optional"
                      className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Supplier
                    </label>
                    <input
                      type="text"
                      value={bulkSupplier}
                      onChange={(e) => setBulkSupplier(e.target.value)}
                      placeholder="Optional"
                      className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-panel-dark">
                      <tr className="border-b border-panel-border">
                        <th className="text-left py-2 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                          Product
                        </th>
                        <th className="text-right py-2 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                          Current
                        </th>
                        <th className="text-right py-2 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                          New Price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {latestPrices.map((pp) => (
                        <tr
                          key={pp.product.id}
                          className="border-b border-panel-border/50"
                        >
                          <td className="py-2 px-4">
                            <div className="font-mono text-text-primary text-sm">
                              {pp.product.name}
                            </div>
                          </td>
                          <td className="py-2 px-4 text-right font-mono text-text-tertiary text-sm">
                            {pp.rackPrice ? formatPrice(pp.rackPrice.price) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right">
                            <input
                              type="number"
                              step="0.0001"
                              value={bulkPrices[pp.product.id] || ''}
                              onChange={(e) =>
                                setBulkPrices({
                                  ...bulkPrices,
                                  [pp.product.id]: e.target.value,
                                })
                              }
                              className="w-28 bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                              placeholder="0.0000"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end space-x-4 pt-4 border-t border-panel-border">
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="px-6 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors font-mono uppercase text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkSave}
                    disabled={isSaving}
                    className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-2 px-6 transition-all uppercase tracking-wider disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Prices'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number | string;
  status?: 'operational' | 'warning' | 'critical';
}

function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-3xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default RackPricesPage;
